/**
 * TranspilerService - ESBuild WASM 기반 브라우저 내 JSX/TSX 트랜스파일러
 *
 * 기능:
 * - JSX/TSX → JavaScript 변환
 * - React/Tailwind CSS 지원
 * - 브라우저 내 실시간 변환
 */

import * as esbuild from 'esbuild-wasm';

let initialized = false;
let initPromise: Promise<void> | null = null;

/**
 * ESBuild WASM 초기화
 * 한 번만 실행되며, 이후 호출은 캐시된 Promise 반환
 */
export async function initializeEsbuild(): Promise<void> {
  if (initialized) return;

  if (initPromise) {
    return initPromise;
  }

  initPromise = esbuild.initialize({
    wasmURL: 'https://unpkg.com/esbuild-wasm@0.20.0/esbuild.wasm',
  });

  await initPromise;
  initialized = true;
}

export interface TranspileResult {
  success: boolean;
  code?: string;
  error?: string;
}

/**
 * JSX/TSX 코드를 JavaScript로 변환
 */
export async function transpileCode(
  code: string,
  language: 'jsx' | 'tsx' | 'js' | 'ts' = 'tsx'
): Promise<TranspileResult> {
  try {
    await initializeEsbuild();

    const result = await esbuild.transform(code, {
      loader: language,
      jsx: 'automatic',
      target: 'es2020',
      format: 'esm',
    });

    return {
      success: true,
      code: result.code,
    };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : String(error),
    };
  }
}

/**
 * React 컴포넌트를 렌더링 가능한 HTML로 빌드
 */
export function buildPreviewHtml(
  transpiledCode: string,
  options: {
    title?: string;
    theme?: 'light' | 'dark';
    tailwindEnabled?: boolean;
  } = {}
): string {
  const { title = 'Preview', theme = 'light', tailwindEnabled = true } = options;

  const tailwindScript = tailwindEnabled
    ? '<script src="https://cdn.tailwindcss.com"></script>'
    : '';

  const themeClass = theme === 'dark' ? 'dark' : '';

  return `<!DOCTYPE html>
<html lang="ko" class="${themeClass}">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${title}</title>
  ${tailwindScript}
  <script type="importmap">
    {
      "imports": {
        "react": "https://esm.sh/react@18.2.0",
        "react-dom": "https://esm.sh/react-dom@18.2.0",
        "react-dom/client": "https://esm.sh/react-dom@18.2.0/client",
        "react/jsx-runtime": "https://esm.sh/react@18.2.0/jsx-runtime"
      }
    }
  </script>
  <style>
    * {
      margin: 0;
      padding: 0;
      box-sizing: border-box;
    }
    body {
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, sans-serif;
    }
    #root {
      min-height: 100vh;
    }
    .error-container {
      padding: 20px;
      background: #fef2f2;
      border: 1px solid #fecaca;
      border-radius: 8px;
      margin: 20px;
    }
    .error-title {
      color: #dc2626;
      font-weight: 600;
      margin-bottom: 8px;
    }
    .error-message {
      color: #7f1d1d;
      font-family: monospace;
      font-size: 14px;
      white-space: pre-wrap;
    }
  </style>
</head>
<body>
  <div id="root"></div>
  <script type="module">
    import React from 'react';
    import { createRoot } from 'react-dom/client';

    // 에러 핸들링
    window.onerror = (message, source, lineno, colno, error) => {
      showError(message);
      return true;
    };

    window.onunhandledrejection = (event) => {
      showError(event.reason?.message || event.reason);
    };

    function showError(message) {
      const root = document.getElementById('root');
      root.innerHTML = \`
        <div class="error-container">
          <div class="error-title">런타임 에러</div>
          <div class="error-message">\${escapeHtml(String(message))}</div>
        </div>
      \`;
    }

    function escapeHtml(text) {
      const div = document.createElement('div');
      div.textContent = text;
      return div.innerHTML;
    }

    // 실행 타임아웃 (무한 루프 방지)
    const timeout = setTimeout(() => {
      showError('실행 시간 초과 (5초). 무한 루프가 있을 수 있습니다.');
    }, 5000);

    try {
      ${transpiledCode}

      // App 컴포넌트 찾기
      const AppComponent = typeof App !== 'undefined' ? App : null;

      if (AppComponent) {
        const root = createRoot(document.getElementById('root'));
        root.render(React.createElement(AppComponent));
        clearTimeout(timeout);
      } else {
        showError('App 컴포넌트를 찾을 수 없습니다. "export default function App()" 또는 "function App()" 형태로 정의해주세요.');
        clearTimeout(timeout);
      }
    } catch (error) {
      showError(error.message || error);
      clearTimeout(timeout);
    }
  </script>
</body>
</html>`;
}

/**
 * React 기본 템플릿 코드
 */
export const REACT_TEMPLATE = `function App() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-8">
      <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full">
        <h1 className="text-2xl font-bold text-gray-800 mb-4">
          Hello, Prototype!
        </h1>
        <p className="text-gray-600 mb-6">
          이 프로토타입을 수정하여 UI를 테스트해보세요.
        </p>
        <button
          className="w-full bg-blue-600 text-white py-3 px-6 rounded-lg font-medium hover:bg-blue-700 transition-colors"
          onClick={() => alert('버튼 클릭!')}
        >
          클릭하세요
        </button>
      </div>
    </div>
  );
}
`;

/**
 * 에러 메시지 파싱
 */
export function parseTranspileError(error: string): {
  line?: number;
  column?: number;
  message: string;
} {
  // ESBuild 에러 형식: "filename:line:column: error: message"
  const match = error.match(/:(\d+):(\d+):\s*error:\s*(.+)/);
  if (match) {
    return {
      line: parseInt(match[1], 10),
      column: parseInt(match[2], 10),
      message: match[3],
    };
  }
  return { message: error };
}
